import { useEffect, useState, type ReactNode } from 'react'
import { useConnectionState } from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'
import { Clock } from 'lucide-react'
import { cn } from '@/custolink-newdesign/lib/utils'
import { formatElapsed } from './roomTime'

/**
 * Ticks once per second from `anchorMs` (see roomTime.ts's elapsedAnchorFromAgo), null while
 * there is no anchor yet. Cleared on unmount.
 */
export function useElapsedSince(anchorMs: number | null): number | null {
  const [seconds, setSeconds] = useState<number | null>(
    anchorMs == null ? null : Math.floor((Date.now() - anchorMs) / 1000),
  )

  useEffect(() => {
    if (anchorMs == null) {
      setSeconds(null)
      return
    }
    const tick = () => setSeconds(Math.floor((Date.now() - anchorMs) / 1000))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [anchorMs])

  return seconds
}

const CONNECTION_LABEL: Record<
  ConnectionState,
  { label: string; dotClassName: string; pulse?: boolean }
> = {
  [ConnectionState.Connected]: { label: 'Connected', dotClassName: 'cl:bg-emerald-500' },
  [ConnectionState.Connecting]: { label: 'Connecting', dotClassName: 'cl:bg-amber-500' },
  [ConnectionState.Reconnecting]: {
    label: 'Reconnecting',
    dotClassName: 'cl:bg-amber-500',
    pulse: true,
  },
  [ConnectionState.SignalReconnecting]: {
    label: 'Reconnecting',
    dotClassName: 'cl:bg-amber-500',
    pulse: true,
  },
  [ConnectionState.Disconnected]: { label: 'Disconnected', dotClassName: 'cl:bg-red-500' },
}

/**
 * Slim session header for the remote-support room shell (D-02): title/subtitle identity, a live
 * connection-state label, elapsed time, and the recording notice. Must be mounted inside
 * LiveKitRoom - it reads useConnectionState() from context.
 */
export function RoomHeader(p: {
  title: string
  subtitle?: string
  details?: string[]
  elapsedAnchorMs: number | null
  recording: boolean
  compact?: boolean
  trailing?: ReactNode
  /** Docks the recording stop action into the header (mobile); the floating pill is suppressed. */
  onStopRecording?: () => void
  stopRecordingDisabled?: boolean
}) {
  const connectionState = useConnectionState()
  const connection = CONNECTION_LABEL[connectionState] ?? CONNECTION_LABEL[ConnectionState.Disconnected]
  const elapsedSeconds = useElapsedSince(p.elapsedAnchorMs)

  const subline = [p.subtitle, ...(p.details ?? [])].filter(
    (v): v is string => v != null && v !== '',
  )

  return (
    <div
      className={cn(
        'cl:flex cl:items-center cl:gap-3 cl:bg-neutral-950/95 cl:text-white cl:border-b cl:border-white/10 cl:px-3',
        p.compact
          ? 'cl:min-h-[44px] cl:pt-[env(safe-area-inset-top)] cl:text-xs'
          : 'cl:h-12',
        // A phone held sideways gives the compact header only the room's 88px side column.
        p.compact &&
          'cl:phone-landscape:flex-col cl:phone-landscape:items-stretch cl:phone-landscape:gap-2 cl:phone-landscape:px-2 cl:phone-landscape:py-2',
      )}
    >
      <div className="cl:min-w-0 cl:flex-1">
        <p
          className={cn(
            'cl:truncate cl:font-semibold',
            p.compact ? 'cl:text-sm' : 'cl:text-base',
          )}
        >
          {p.title}
        </p>
        {subline.length > 0 && (
          <p className="cl:text-white/70 cl:text-xs cl:truncate">{subline.join(' · ')}</p>
        )}
      </div>

      <div
        className={cn(
          'cl:flex cl:shrink-0 cl:items-center cl:gap-3',
          p.compact && 'cl:phone-landscape:flex-col cl:phone-landscape:items-start cl:phone-landscape:gap-1.5',
        )}
      >
        {p.recording && p.onStopRecording != null ? (
          // Docked recording control: the dot carries the "recording" status and the button is
          // the stop action, in one element so the compact phone header does not grow a row.
          <button
            type="button"
            aria-label="Stop recording"
            disabled={p.stopRecordingDisabled}
            className="cl:flex cl:min-h-[44px] cl:items-center cl:justify-center cl:gap-2 cl:rounded-md cl:bg-red-600 cl:px-2.5 cl:text-xs cl:font-semibold cl:text-white cl:hover:bg-red-700 cl:disabled:opacity-50 cl:disabled:pointer-events-none cl:focus-visible:outline-2 cl:focus-visible:outline-white"
            onClick={p.onStopRecording}
          >
            <span className="cl:size-2 cl:rounded-full cl:bg-white" aria-hidden="true" />
            Stop
          </button>
        ) : p.recording ? (
          <span
            role="status"
            className="cl:flex cl:items-center cl:gap-1.5 cl:text-xs cl:font-medium cl:text-red-400"
          >
            <span className="cl:size-2 cl:rounded-full cl:bg-red-500" />
            {p.compact ? 'Recording' : 'This call is being recorded'}
          </span>
        ) : null}

        {elapsedSeconds != null && (
          <span
            title="Elapsed time since both sides joined"
            className="cl:flex cl:items-center cl:gap-1 cl:text-xs cl:tabular-nums cl:text-white/80"
          >
            <Clock className="cl:size-3.5" />
            {formatElapsed(elapsedSeconds)}
          </span>
        )}

        <span
          aria-live="polite"
          role="status"
          className="cl:flex cl:items-center cl:gap-1.5 cl:text-xs cl:text-white/80"
        >
          <span
            className={cn(
              'cl:size-2 cl:rounded-full',
              connection.dotClassName,
              connection.pulse && 'cl:motion-safe:animate-pulse',
            )}
          />
          {connection.label}
        </span>

        {p.trailing}
      </div>
    </div>
  )
}
